import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FaUser } from 'react-icons/fa';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import { useAuth } from '../context/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import FilterButton from './FilterButton';
import getMenuItems, { getActiveMenuPath } from '../constants/menuItems';

const Header = ({ headerTitle = 'vTest' }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, role, name, email } = useAuth();

    const [currentMenu, setCurrentMenu] = useState(getActiveMenuPath(location.pathname));
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const profileMenuRef = useRef(null);

    // Keep active tab in sync when navigating (e.g. via browser back)
    useEffect(() => {
        setCurrentMenu(getActiveMenuPath(location.pathname));
    }, [location.pathname]);

    // Close profile dropdown when clicking outside
    useEffect(() => {
        if (!showProfileMenu) return;
        const handleClickOutside = (event) => {
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showProfileMenu]);

    const handleLogout = () => {
        setShowProfileMenu(false);
        setIsLogoutModalOpen(true);
    };

    const confirmLogout = async () => {
        try {
            await logout();
            toast.success('Logged out successfully');
        } catch (error) {
            console.error('Logout error:', error);
            toast.success('Logged out');
        } finally {
            setIsLogoutModalOpen(false);
        }
    };

    const cancelLogout = () => {
        setIsLogoutModalOpen(false);
    };

    const menuItems = getMenuItems(role);

    return (
        <>
            <header className="bg-campus-surface border-b border-campus-border sticky top-0 z-50 shadow-soft">
                <div className="flex items-center justify-between px-4 py-0 h-16">
                    {/* Left — Logo */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <img src={logo} alt="Logo" className="h-8 w-auto" />
                    </div>

                    {/* Center — Dynamic page name */}
                    {headerTitle && (
                        <div className="absolute left-1/2 transform -translate-x-1/2 pointer-events-none">
                            <span className="text-base font-heading font-semibold tracking-heading text-campus-text-primary">
                                {headerTitle}
                            </span>
                        </div>
                    )}

                    {/* Right — Navigation tabs + profile icon */}
                    <div className="flex items-center gap-2 relative flex-shrink-0" ref={profileMenuRef}>
                        {/* Tab bar */}
                        {menuItems.length > 0 && (
                            <div className="flex gap-1 border-2 border-solid border-gray-800 rounded-full p-1 bg-campus-surface mr-4">
                                {menuItems.map((item) => (
                                    <FilterButton
                                        key={item.path}
                                        label={item.name}
                                        value={item.path}
                                        currentFilter={currentMenu}
                                        onClick={() => {
                                            navigate(item.path);
                                            setCurrentMenu(item.path);
                                        }}
                                    />
                                ))}
                            </div>
                        )}

                        {/* Profile icon */}
                        <button
                            type="button"
                            className="flex items-center justify-center bg-campus-text-primary p-2 rounded-full cursor-pointer hover:bg-campus-text-body transition-colors duration-200"
                            onClick={() => setShowProfileMenu((prev) => !prev)}
                            title="Profile menu"
                            aria-label="Open profile menu"
                            aria-expanded={showProfileMenu}
                        >
                            <FaUser size={20} color="white" />
                        </button>

                        {/* Dropdown */}
                        {showProfileMenu && (
                            <div className="absolute right-0 top-full mt-2 bg-campus-surface border border-campus-border rounded-xl shadow-hover z-50 min-w-[210px] py-2 animate-fade-slide">
                                {/* User Info Section */}
                                <div className="px-4 pb-2 mb-2 border-b border-campus-border">
                                    <p className="text-sm font-bold text-campus-text-primary truncate">
                                        {name || 'User'}
                                    </p>
                                    <p className="text-xs text-campus-text-body truncate">
                                        {email || 'No email'}
                                    </p>
                                </div>
                                {/* Actions */}
                                <div className="px-2">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full text-left px-3 py-2 text-sm text-campus-primary font-medium hover:bg-red-50 rounded-lg transition-colors duration-200"
                                    >
                                        Logout
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <ConfirmationModal
                isOpen={isLogoutModalOpen}
                title="Logout Confirmation"
                message="Are you sure you want to logout?"
                onConfirm={confirmLogout}
                onCancel={cancelLogout}
                confirmText="Logout"
                cancelText="Cancel"
                confirmButtonClass="bg-campus-primary hover:bg-campus-primary-dark"
            />
        </>
    );
};

Header.propTypes = {
    headerTitle: PropTypes.string,
};

export default Header;
